import { type Context, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type { EditorContextPayloadData } from '@atlaskit/rovo-triggers/types';
import {
	useChatContextAIFeatureContext,
	useChatContextDatabaseContext,
	useChatContextEditorContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store';

export enum PageContextType {
	BITBUCKET = 'bitbucket',
	CONFLUENCE_HOME = 'confluence-home',
	CONFLUENCE_PAGE_VIEW = 'confluence-page-view',
	CONFLUENCE_PAGE_EDIT = 'confluence-page-edit',
	CONFLUENCE_WHITEBOARD = 'confluence-whiteboard',
	CONFLUENCE_DATABASE = 'confluence-database',
	JIRA_HOME = 'jira-home',
	JIRA_ISSUE_VIEW = 'jira-issue-view',
	JIRA_BACKLOG = 'jira-backlog',
	JIRA_BOARD = 'jira-board-view',
	JIRA_PROJECT = 'jira-project',
	JIRA_SERVICE_DESK = 'jira-service-desk',
	JIRA_PLAN = 'jira-plan',
	JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY = 'jira-cmp-space-settings-people-legacy',
	JIRA_CMP_SPACE_SETTINGS_PERMISSIONS_LEGACY = 'jira-cmp-space-settings-permissions-legacy',
	JIRA_ISSUES_LIST = 'jira-issues-list',
	JIRA_ITAM_HAM = 'jira-itam-ham',
	ATLAS_HOME = 'atlas-home',
	ATLAS_PROJECT = 'atlas-project',
	ATLAS_GOAL = 'atlas-goal',
	FEEDBACK_APP = 'feedback-app',
	STORE_HOME = 'store-home',
	TRELLO_BOARD = 'trello-board',
	TRELLO_CARD = 'trello-card',
	TEAM_PROFILE = 'team-profile',
	USER_PROFILE = 'user-profile',
	UNKNOWN = 'unknown',
}

export const WindowLocationContext: Context<string | null> = createContext<string | null>(null);

export const useWindowLocation = (): string => {
	const [location, setLocation] = useState(window.location.href);
	// For easier mocking
	const contextValue = useContext(WindowLocationContext);

	const handleLocationChange = useCallback(() => {
		if (window.location.href !== location) {
			setLocation(window.location.href);
		}
	}, [location, setLocation]);

	useEffect(() => {
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(this, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(this, args);
			handleLocationChange();
		};

		const unbind = bindAll(window, [
			{
				type: 'popstate',
				listener: handleLocationChange,
			},
		]);

		return () => {
			unbind();
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, [handleLocationChange]);

	return contextValue ?? location;
};

export enum UseCase {
	THINKING_VIEW = 'thinking-view',
	AUTOCOMPLETE = 'autocomplete',
}

const ALLOWED_PAGE_CONTEXT_TYPES: Record<UseCase, PageContextType[]> = {
	[UseCase.THINKING_VIEW]: [
		PageContextType.CONFLUENCE_PAGE_EDIT,
		PageContextType.CONFLUENCE_PAGE_VIEW,
		PageContextType.CONFLUENCE_WHITEBOARD,
		PageContextType.CONFLUENCE_DATABASE,
		PageContextType.JIRA_ISSUE_VIEW,
		PageContextType.JIRA_SERVICE_DESK,
		PageContextType.JIRA_PROJECT,
		PageContextType.JIRA_PLAN,
		PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY,
		PageContextType.JIRA_CMP_SPACE_SETTINGS_PERMISSIONS_LEGACY,
		PageContextType.JIRA_ISSUES_LIST,
		PageContextType.JIRA_ITAM_HAM,
		PageContextType.ATLAS_PROJECT,
		PageContextType.ATLAS_GOAL,
		PageContextType.FEEDBACK_APP,
		PageContextType.STORE_HOME,
		PageContextType.TRELLO_BOARD,
		PageContextType.TRELLO_CARD,
		PageContextType.TEAM_PROFILE,
		PageContextType.USER_PROFILE,
	],
	[UseCase.AUTOCOMPLETE]: [
		PageContextType.BITBUCKET,
		PageContextType.CONFLUENCE_HOME,
		PageContextType.CONFLUENCE_PAGE_EDIT,
		PageContextType.CONFLUENCE_PAGE_VIEW,
		PageContextType.CONFLUENCE_WHITEBOARD,
		PageContextType.CONFLUENCE_DATABASE,
		PageContextType.JIRA_BACKLOG,
		PageContextType.JIRA_HOME,
		PageContextType.JIRA_ISSUE_VIEW,
		PageContextType.ATLAS_HOME,
		PageContextType.STORE_HOME,
		PageContextType.TRELLO_BOARD,
		PageContextType.TRELLO_CARD,
	],
};

const FALLBACK_PAGE_CONTEXT_TYPE: Record<UseCase, PageContextType> = {
	[UseCase.THINKING_VIEW]: PageContextType.UNKNOWN,
	[UseCase.AUTOCOMPLETE]: PageContextType.ATLAS_HOME,
};

export const determinePageType = (
	location: string,
	useCase: UseCase = UseCase.THINKING_VIEW,
): PageContextType => {
	const allowedTypes = new Set(ALLOWED_PAGE_CONTEXT_TYPES[useCase]);

	const allUrlPatterns: [PageContextType, RegExp][] = [
		[PageContextType.BITBUCKET, /bitbucket\.org\/.*/],
		//If flag is on, use confluence page view for confluence home url pattern since model doesn't have enough data for home
		...(useCase === UseCase.AUTOCOMPLETE && fg('rovo_chat_typeahead_use_confluence-page_fallback')
			? [[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/home$/] as [PageContextType, RegExp]]
			: [[PageContextType.CONFLUENCE_HOME, /\/wiki\/home$/] as [PageContextType, RegExp]]),
		[PageContextType.CONFLUENCE_PAGE_EDIT, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\/edit-v2\//],
		[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\//],
		// Support whiteboard url
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/wiki\/spaces\/[^\/]+\/whiteboard\//],
		// Support whiteboard iframe url
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/whiteboards\/whiteboard\/[^\/\?]+/],
		// Support whiteboard internal url after spa transition
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/whiteboard\/[a-f0-9-]+/],
		// Support database url
		[PageContextType.CONFLUENCE_DATABASE, /\/wiki\/spaces\/[^\/]+\/database\//],
		// Support database internal url after spa transition
		[PageContextType.CONFLUENCE_DATABASE, /\/database\/[a-f0-9-]+/],
		[PageContextType.JIRA_BACKLOG, /\/jira\/.*\/backlog$/],
		[PageContextType.JIRA_BOARD, /\/jira\/.*\/boards\//],

		[PageContextType.JIRA_ISSUE_VIEW, /\/browse\/[^\/]+$/],
		[PageContextType.JIRA_ISSUE_VIEW, /\/jira\/.+\/projects\/.+\?selectedIssue/],
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		[
			PageContextType.JIRA_SERVICE_DESK,
			/\/jira(?:\/.+)?\/servicedesk\/projects\/.+\/queues\/issue\//,
		],
		[PageContextType.JIRA_PROJECT, /\/jira\/.+\/projects\//],
		...(fg('rovo_chat_support_issues_list_context_banner')
			? [[PageContextType.JIRA_ISSUES_LIST, /\/issues\/\?jql=/] as [PageContextType, RegExp]]
			: []),
		...(fg('itam_ftw')
			? [[PageContextType.JIRA_ITAM_HAM, /\/jira\/itam\/ham(\/|$)/] as [PageContextType, RegExp]]
			: []),
		...(fg('rovo_chat_jira_plans_minion')
			? [[PageContextType.JIRA_PLAN, /\/jira\/plans\/\d+\//] as [PageContextType, RegExp]]
			: []),
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		[
			PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY,
			/\/plugins\/servlet\/project-config\/[^\/]+\/people/,
		],
		[
			PageContextType.JIRA_CMP_SPACE_SETTINGS_PERMISSIONS_LEGACY,
			/\/plugins\/servlet\/project-config\/[^\/]+\/permissions/,
		],
		// atlas home is either home.atlassian.com, home.stg.atlassian.com, team.atlassian.com, or team.stg.atlassian.com
		[PageContextType.ATLAS_HOME, /\/(home|team)\./],
		[PageContextType.ATLAS_PROJECT, /\/(home|team).+\/project\/ATLAS-/],
		[PageContextType.ATLAS_GOAL, /\/(home|team).+\/goal\/ATLAS-/],
		// Store and Marketplace surfaces can appear on both internal and production domains.
		[
			PageContextType.STORE_HOME,
			/(?:store\.[^/]*atl-paas\.net|store\.atlassian\.com|mpac\.atlassian\.com).*\/(?:objects|categories)\//,
		],
		[PageContextType.TEAM_PROFILE, /\/(home|team)\..+\/people\/team/],
		[PageContextType.USER_PROFILE, /\/(home|team)\..+\/people\/(?!team)/],
		[PageContextType.FEEDBACK_APP, /\/feedback-app/],
		// Trello: trello.com and trellis.coffee (enterprise)
		[PageContextType.TRELLO_CARD, /(?:trello\.com|trellis\.coffee)\/c\/[^/]+/],
		[PageContextType.TRELLO_BOARD, /(?:trello\.com|trellis\.coffee)\/b\/[^/]+/],
	];

	const urlPatterns = allUrlPatterns.filter(([type]) => allowedTypes.has(type));

	for (const [type, pattern] of urlPatterns) {
		const matched = pattern.test(location);
		if (!matched) {
			continue;
		}

		return type;
	}

	return FALLBACK_PAGE_CONTEXT_TYPE[useCase];
};

export const usePageContextType = (useCase: UseCase = UseCase.THINKING_VIEW): PageContextType => {
	const location = useWindowLocation();
	const [pageContextType, setPageContextType] = useState<PageContextType>(
		determinePageType(location, useCase),
	);

	useEffect(() => {
		setPageContextType(determinePageType(location, useCase));
	}, [location, useCase]);

	return pageContextType;
};

export type CurrentEditorContext = {
	type: 'editor_context';
	payload: EditorContextPayloadData;
};

export type CurrentPageContext = {
	type: 'current_page';
	pageContextType: PageContextType;
};

export type CurrentDatabaseContext = {
	type: 'database_context';
	selectedElementIds: string[];
};

export type JiraViewContextData = {
	url?: string;
	selectedIssueIdentifiers: string[];
	selectedIssuesMetadata?: Array<{ id: string; key: string; summary?: string }>;
	viewType?: 'list' | 'backlog';
	jqlStrings?: string[];
	filterIdentifiers?: string[];
	projectIdentifiers?: string[];
};

export type None = {
	type: 'none';
};

export type CurrentJiraViewContext = {
	type: 'jira_view_context';
	payload: JiraViewContextData;
};

export type CurrentPageContextType =
	| CurrentEditorContext
	| CurrentJiraViewContext
	| CurrentPageContext
	| CurrentDatabaseContext
	| None;

const JIRA_VIEW_CONTEXT_KEY = 'jira_view_context';

function hasValidJiraViewContextData(value: unknown): value is JiraViewContextData {
	if (value == null || typeof value !== 'object') {
		return false;
	}
	const selected = Object.getOwnPropertyDescriptor(value, 'selectedIssueIdentifiers')?.value;
	return Array.isArray(selected);
}

export const useCurrentPageAsContext = (): {
	canUseCurrentPageContextUserPreference: (key: string) => boolean;
	setUseCurrentPageContextUserPreference: (key: string, isEnabled: boolean) => void;
	currentContext: CurrentPageContextType;
} => {
	const pageContextType = usePageContextType();
	const { getChatMode, setUseCurrentPageContext } = useChatModeStoreActions();
	const [editorContext] = useChatContextEditorContext();
	const [databaseContext] = useChatContextDatabaseContext();
	const [aiFeatureContext] = useChatContextAIFeatureContext();
	const rawJiraViewContext = aiFeatureContext?.[JIRA_VIEW_CONTEXT_KEY];
	const jiraViewContextData = hasValidJiraViewContextData(rawJiraViewContext)
		? rawJiraViewContext
		: undefined;

	let currentContext: CurrentPageContextType = {
		type: 'none',
	};

	if (editorContext) {
		// prefer editor context over page context if available
		currentContext = {
			type: 'editor_context',
			payload: editorContext,
		};
	} else if (databaseContext) {
		currentContext = {
			type: 'database_context',
			selectedElementIds: databaseContext.selectedElementIds ?? [],
		};
	} else if (
		jiraViewContextData &&
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue(
			'jira-ai-jira-view-context-in-rovo-chat-experiment',
			'isEnabled',
			false,
		)
	) {
		currentContext = {
			type: 'jira_view_context',
			payload: jiraViewContextData,
		};
	} else if (pageContextType !== PageContextType.UNKNOWN) {
		// if no editor context, use page context if available
		currentContext = {
			type: 'current_page',
			pageContextType,
		};
	}

	return {
		canUseCurrentPageContextUserPreference: (key: string): boolean => {
			return getChatMode(key).useCurrentPageContext;
		},
		setUseCurrentPageContextUserPreference: setUseCurrentPageContext,
		currentContext,
	};
};
